import { useRef } from 'react';
import { Badge, Table, Spinner, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchClientPortalInvoices, CLIENT_PORTAL_PAGE_SIZE } from '../api/clientPortalApi';
import { ClientPortalLayout } from '../components/ClientPortalLayout';
import { TablePageLayout } from '../components/TablePageLayout';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

export function ClientPortalInvoicesPage() {
  const loadMoreRef = useRef(null);

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetched,
  } = useInfiniteQuery({
    queryKey: ['client-portal-invoices'],
    queryFn: fetchClientPortalInvoices,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentCount = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return lastPage.results.length === CLIENT_PORTAL_PAGE_SIZE ? currentCount : undefined;
    },
  });

  const invoices = data?.pages.flatMap((page) => page.results) || [];

  const { containerRef, handleScroll } = useScrollRestoration(
    'clientPortalInvoicesScroll',
    isFetched
  );

  useInfiniteScroll({
    loadMoreRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef: containerRef,
    dependencyArray: [invoices.length]
  });

  return (
    <ClientPortalLayout>
      <TablePageLayout
        title="All invoices"
        description="View all your invoices."
        isLoading={isLoading && invoices.length === 0}
        isError={isError}
        error={error}
        containerRef={containerRef}
        onScroll={handleScroll}
      >
        <Table responsive striped hover className="mb-0">
          <thead>
            <tr>
              <th>Number</th>
              <th>Company</th>
              <th>Origin</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Total</th>
              <th>Due</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && !isLoading ? (
              <tr>
                <td colSpan="8" className="text-center py-4 text-muted">
                  No invoices found.
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.name}>
                  <td>
                    <div className="d-inline-flex align-items-center gap-2">
                      <Link to={`/client/invoices/${invoice.name}`}>{invoice.name}</Link>
                      {invoice.download_link && (
                        <a href={invoice.download_link} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          <Badge bg="success">PDF</Badge>
                        </a>
                      )}
                    </div>
                  </td>
                  <td>{invoice.partner?.name || '-'}</td>
                  <td>
                    {invoice.invoice_origin && invoice.invoice_origin.startsWith('SO') ? (
                      <Link to={`/client/orders/${invoice.invoice_origin}`}>{invoice.invoice_origin}</Link>
                    ) : (
                      invoice.invoice_origin !== "False" ? invoice.invoice_origin : '-'
                    )}
                  </td>
                  <td>{formatDate(invoice.invoice_date)}</td>
                  <td>{formatDate(invoice.invoice_date_due)}</td>
                  <td>{formatCurrency(invoice.amount_total_client_signed)}</td>
                  <td>{formatCurrency(invoice.amount_residual_client_signed)}</td>
                  <td><PaymentStatusBadge state={invoice.payment_state} /></td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {(hasNextPage || isFetchingNextPage) && (
          <div ref={loadMoreRef} className="text-center py-4 border-top">
            {isFetchingNextPage ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading more...
              </>
            ) : (
              <Button variant="link" onClick={() => fetchNextPage()}>
                Load more
              </Button>
            )}
          </div>
        )}
      </TablePageLayout>
    </ClientPortalLayout>
  );
}
